import { useEffect, useState } from 'react';
import { useClearRefinements, useSortBy } from 'react-instantsearch-core';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { LegacyButton } from '@/components/LegacyButton';
import type { WatchCollectionFilterProps } from '@/components/ListingFilters/components/WatchCollectionFilter';
import { Overlay } from '@/components/Overlay';
import { SectionHeader } from '@/components/SectionHeader';
import { useListingSortBy } from '@/hooks/useListingSortBy';
import { useListingState } from '@/hooks/useListingState';
import { commonMessages } from '@/messages/common';
import { listingActions } from '@/states/listingState';

import { Filters } from '../Filters/Filters';

import * as Sc from '../../../Drawer/Drawer.styles';
import * as S from './FiltersModal.styles';

export type FiltersModalProps = Pick<
  WatchCollectionFilterProps,
  'collectionData'
> & {
  /**
   * Force mount the modal content even if it hasn't been opened.
   * This is needed when we have URL filters that need widgets to be registered
   * with InstantSearch for the search query to include those filters.
   */
  forceMount?: boolean;
  isWidget?: boolean;
  showCollectionFilter?: boolean;
  showMaterialsFilter?: boolean;
};

/**
 * Inner content component that contains the Algolia hooks.
 * This is only mounted when the modal has been opened at least once,
 * to prevent the hooks from registering on initial page load and
 * triggering an additional Algolia search.
 */
const FiltersModalContent = ({
  collectionData,
  isOpen,
  isWidget = false,
  showCollectionFilter = false,
  showMaterialsFilter = false,
}: FiltersModalProps & { isOpen: boolean }) => {
  const { formatMessage } = useIntl();
  const { canRefine, refine: clearFilters } = useClearRefinements();
  const items = useListingSortBy();
  const { options, refine } = useSortBy({ items });

  const closeModal = () => {
    window.scrollTo({
      top: 0,
    });
    if (isWidget) {
      window.parentIFrame?.autoResize(true);
    }
    listingActions.updateModalState({ status: 'CLOSE', openedTab: null });
  };

  const handleClear = () => {
    const defaultSort = options.length > 0 ? options[0].value : null;
    if (defaultSort) {
      refine(defaultSort);
    }

    if (canRefine) {
      clearFilters();
    }

    closeModal();
  };

  // Determine whether the browser is Edge. In the Edge browser, there may be an obstruction at the bottom.
  const [isEdge, setIsEdge] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent;
    if (userAgent?.includes('Edg')) {
      setIsEdge(true);
    }
  }, []);

  return (
    <Overlay
      className={isOpen ? 'modal-open' : ''}
      data-test-id='FiltersModalOverlay'
      onClick={closeModal}
      style={{ display: isOpen ? 'block' : 'none' }}
    >
      <Sc.Wrapper $side='left' $top={1} data-test-id='FiltersModalWrapper'>
        <Sc.HeaderWrapper data-test-id='FiltersModalHeaderWrapper'>
          <Sc.Header data-test-id='FiltersModalHeader'>
            <SectionHeader
              color='black'
              colorized={false}
              disableGutters
              htmlElementTag='h2'
              sizeDesktop={24}
              sizeMobile={24}
              text={formatMessage(commonMessages.filters)}
              textAlign='center'
            />
          </Sc.Header>
          <Sc.CloseButton
            data-testid='FiltersModalCloseButton'
            onClick={closeModal}
          >
            <Sc.CloseIcon data-test-id='FiltersModalCloseIcon' />
          </Sc.CloseButton>
        </Sc.HeaderWrapper>

        <Sc.Body data-test-id='FiltersModalBody'>
          <Box id='filters-modal-body' mb={10} p={1}>
            <Filters
              collectionData={collectionData}
              showCollectionFilter={showCollectionFilter}
              showMaterialsFilter={showMaterialsFilter}
            />
          </Box>
        </Sc.Body>

        <Sc.Footer data-test-id='FiltersModalFooter'>
          <S.FooterWrapper
            $isEdge={isEdge}
            data-test-id='FiltersModalFooterWrapper'
          >
            <S.ButtonsFooter gridGap={2}>
              <LegacyButton
                data-test-id='FiltersModalClearButton'
                isFluid
                label={formatMessage(commonMessages.reset)}
                onClick={handleClear}
                variant='outlined'
              />
              <LegacyButton
                data-test-id='FiltersModalApplyButton'
                isFluid
                label={formatMessage(commonMessages.apply)}
                onClick={closeModal}
              />
            </S.ButtonsFooter>
          </S.FooterWrapper>
        </Sc.Footer>
      </Sc.Wrapper>
    </Overlay>
  );
};

/**
 * FiltersModal component that lazy-loads its content to prevent
 * Algolia hooks from registering on initial page load.
 */
export const FiltersModal = ({ forceMount, ...props }: FiltersModalProps) => {
  const { modalState } = useListingState();
  const isOpen = modalState.status === 'OPEN';

  // Track if modal has ever been opened to enable lazy mounting
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  }, [isOpen, hasBeenOpened]);

  const shouldMount = forceMount || hasBeenOpened;

  // Don't render anything until the modal has been opened at least once
  // (or forceMount is true). This prevents the Algolia hooks
  // (useSortBy, useClearRefinements) from registering on initial page load,
  // which would trigger an extra search.
  if (!shouldMount) {
    return null;
  }

  return <FiltersModalContent {...props} isOpen={isOpen} />;
};
